import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fetchDashboard } from "../../redux/action";
import { hasPermission } from "../../Utills/permission";
import { packageMap, roleMap } from "../../Utills/enums";
import { PrimaryButton } from "../../component/common/Buttons";
import { ButtonLoader } from "../../component/common/Loader";
import { getCurrentLocale } from "../../i18n/locale";
import "./dashboard.css";

const currency = { format: (value) => new Intl.NumberFormat(getCurrentLocale(), { style: "currency", currency: "INR", minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(value) };

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPresetRange = (preset) => {
  const end = new Date();
  const start = new Date(end);
  if (preset === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (preset === "week") {
    start.setDate(start.getDate() - 6);
  } else if (preset === "month") {
    start.setDate(start.getDate() - 29);
  }
  return { start: toDateInput(start), end: toDateInput(end) };
};

const aggregateSales = (rows = []) => {
  const paidOrders = rows.reduce((sum, item) => sum + Number(item.orders || 0), 0);
  const cancelledOrders = rows.reduce((sum, item) => sum + Number(item.cancelOrders || 0), 0);
  const totalOrders = rows.reduce((sum, item) => sum + Number(item.totalOrders ?? (item.orders || 0) + (item.cancelOrders || 0)), 0);
  const netSales = rows.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
  const cancelledValue = rows.reduce((sum, item) => sum + Number(item.cancelOrderAmount || 0), 0);
  const tableVisits = rows.reduce((sum, item) => sum + Number(item.uniqueTablesServed || 0), 0);
  return {
    paidOrders, cancelledOrders, totalOrders, netSales, cancelledValue, tableVisits,
    averageOrderValue: paidOrders ? netSales / paidOrders : 0,
    cancellationRate: totalOrders ? (cancelledOrders / totalOrders) * 100 : 0,
  };
};

const getComparison = (current, previous, inverse = false) => {
  if (!previous) {
    if (!current) return { label: "No change", tone: "neutral" };
    return { label: "New", tone: inverse ? "negative" : "positive" };
  }
  const percent = ((current - previous) / Math.abs(previous)) * 100;
  if (Math.abs(percent) < 0.05) return { label: "No change", tone: "neutral" };
  return {
    label: `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`,
    tone: (inverse ? percent < 0 : percent > 0) ? "positive" : "negative",
  };
};

const MetricCard = ({ label, value, detail, comparison, tone = "default" }) => (
  <article className={`dashboard-metric dashboard-metric--${tone}`}>
    <div className="dashboard-metric__heading">
      <span>{label}</span>
      {comparison && <span className={`dashboard-change dashboard-change--${comparison.tone}`}>{comparison.label}</span>}
    </div>
    <strong>{value}</strong>
    <small>{detail}</small>
  </article>
);

const EmptyInsight = ({ children }) => <div className="dashboard-empty">{children}</div>;

const Dashboard = () => {
  const dispatch = useDispatch();
  const { dashboard: data = {}, loading } = useSelector((state) => state?.admin || {});
  const user = JSON.parse(localStorage.getItem("admin_token_info") || "{}");
  const role = roleMap[user?.roleId];
  const plan = packageMap[user?.packageId] || "Restaurant Plan";
  const canViewTables = hasPermission(role, plan, "table");
  const canViewTakeaway = hasPermission(role, plan, "takeaway-orders");
  const canViewMenu = hasPermission(role, plan, "menu");
  const initialRange = useMemo(() => getPresetRange("today"), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [activePreset, setActivePreset] = useState("today");
  const today = toDateInput(new Date());

  useEffect(() => {
    dispatch(fetchDashboard(initialRange.start, initialRange.end));
  }, [dispatch, initialRange]);

  const sales = data?.sales || {};
  const salesRows = useMemo(() => Array.isArray(sales.salesData) ? sales.salesData : [], [sales.salesData]);
  const previousRows = useMemo(() => Array.isArray(sales.previousSalesData) ? sales.previousSalesData : [], [sales.previousSalesData]);
  const summary = useMemo(() => aggregateSales(salesRows), [salesRows]);
  const previousSummary = useMemo(() => aggregateSales(previousRows), [previousRows]);
  const takeaway = sales.takeawaySummary || {};
  const topItems = Array.isArray(sales.topSellingItems) ? sales.topSellingItems : [];
  const paymentModes = Array.isArray(sales.revenuePaymentMode) ? sales.revenuePaymentMode : [];
  const categories = Array.isArray(sales.salesByCategory) ? sales.salesByCategory : [];
  const peakHours = Array.isArray(sales.peakHours) ? sales.peakHours : [];
  const trendData = salesRows.map((item) => ({ ...item, day: new Date(item.date).toLocaleDateString(getCurrentLocale(), { day: "numeric", month: "short" }), revenue: Number(item.revenue || 0), orders: Number(item.orders || 0) }));
  const peakHoursData = peakHours.map((item) => ({ hour: new Date(2000, 0, 1, item.hour || 0).toLocaleTimeString(getCurrentLocale(), { hour: "numeric" }), completed: Number(item.orders || 0), cancelled: Number(item.cancelOrders || 0) }));

  const applyPreset = (preset) => {
    const range = getPresetRange(preset);
    setActivePreset(preset);
    setStartDate(range.start);
    setEndDate(range.end);
    dispatch(fetchDashboard(range.start, range.end));
  };

  const applyCustomRange = () => {
    if (!startDate || !endDate || startDate > endDate) return;
    setActivePreset("custom");
    dispatch(fetchDashboard(startDate, endDate));
  };

  const primaryMetrics = [
    { label: "Total Collected", value: currency.format(summary.netSales), detail: "Settlement value from paid bills", comparison: getComparison(summary.netSales, previousSummary.netSales), tone: "sales" },
    { label: "Closed Orders", value: summary.paidOrders.toLocaleString(getCurrentLocale()), detail: "Paid orders in this period", comparison: getComparison(summary.paidOrders, previousSummary.paidOrders), tone: "orders" },
    { label: "Average Order Value", value: currency.format(summary.averageOrderValue), detail: "Collected amount per paid order", comparison: getComparison(summary.averageOrderValue, previousSummary.averageOrderValue), tone: "average" },
    { label: "Cancellation Rate", value: `${summary.cancellationRate.toFixed(1)}%`, detail: `${summary.cancelledOrders} orders · ${currency.format(summary.cancelledValue)}`, comparison: getComparison(summary.cancellationRate, previousSummary.cancellationRate, true), tone: "risk" },
  ];
  const customerOrders = Number(takeaway.customerOrders || 0);
  const staffOrders = Number(takeaway.staffOrders || 0);
  const sourceTotal = customerOrders + staffOrders;
  const customerShare = sourceTotal ? (customerOrders / sourceTotal) * 100 : 0;

  return (
    <div className="dashboard-container restaurant-dashboard">
      <header className="dashboard-hero">
        <div><span className="dashboard-eyebrow">Sales summary</span><h1 className="dashboard-title">Restaurant Dashboard</h1><p className="page-description">Sales, orders and operating performance for the selected period.</p></div>
        <span className="dashboard-plan-badge">{plan}</span>
      </header>

      <section className="dashboard-filter-panel" aria-label="Dashboard date filters">
        <div className="dashboard-presets" aria-label="Quick date ranges">
          {[["today", "Today"], ["yesterday", "Yesterday"], ["week", "Last 7 days"], ["month", "Last 30 days"]].map(([value, label]) => (
            <button type="button" key={value} className={activePreset === value ? "active" : ""} onClick={() => applyPreset(value)}>{label}</button>
          ))}
        </div>
        <div className="dashboard-custom-range">
          <label><span>From</span><input aria-label="Dashboard start date" type="date" max={endDate || today} value={startDate} onChange={(event) => { setStartDate(event.target.value > today ? today : event.target.value); setActivePreset("custom"); }} /></label>
          <label><span>To</span><input aria-label="Dashboard end date" type="date" min={startDate} max={today} value={endDate} onChange={(event) => { setEndDate(event.target.value > today ? today : event.target.value); setActivePreset("custom"); }} /></label>
          <PrimaryButton onClick={applyCustomRange} disabled={loading || !startDate || !endDate || startDate > endDate} aria-busy={loading}>{loading ? <ButtonLoader label="Applying..." /> : "Apply"}</PrimaryButton>
        </div>
      </section>

      <section className="dashboard-section" aria-labelledby="performance-heading">
        <div className="dashboard-section-heading"><div><span>Performance</span><h2 id="performance-heading">Key business metrics</h2></div><small>Compared with the previous equal-length period</small></div>
        <div className="dashboard-metric-grid">{primaryMetrics.map((metric) => <MetricCard key={metric.label} {...metric} />)}</div>
      </section>

      {(canViewTables || canViewTakeaway) && (
        <section className="dashboard-section" aria-labelledby="operations-heading">
          <div className="dashboard-section-heading"><div><span>Operations</span><h2 id="operations-heading">Service overview</h2></div></div>
          <div className="dashboard-operation-grid">
            {canViewTables && <MetricCard label="Table Visits" value={summary.tableVisits.toLocaleString(getCurrentLocale())} detail="Daily distinct tables served" tone="tables" />}
            {canViewTakeaway && <>
              <MetricCard label="Takeaway Orders" value={Number(takeaway.totalOrders || 0).toLocaleString(getCurrentLocale())} detail={`${takeaway.liveOrders || 0} currently active`} tone="takeaway" />
              <MetricCard label="Takeaway Sales" value={currency.format(Number(takeaway.revenue || 0))} detail={`Average ${currency.format(Number(takeaway.averageOrderValue || 0))}`} tone="sales" />
              <MetricCard label="Takeaway Exceptions" value={Number(takeaway.cancelledOrders || 0).toLocaleString(getCurrentLocale())} detail={`${takeaway.refundedOrders || 0} refunded`} tone="risk" />
            </>}
          </div>
        </section>
      )}

      <section className="dashboard-section dashboard-chart-card" aria-labelledby="trend-heading">
        <div className="dashboard-section-heading"><div><span>Trend</span><h2 id="trend-heading">Sales and orders</h2></div></div>
        {trendData.length ? <ResponsiveContainer width="100%" height={340}>
          <ComposedChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="day" /><YAxis yAxisId="sales" tickFormatter={(value) => `₹${value}`} /><YAxis yAxisId="orders" orientation="right" allowDecimals={false} />
            <Tooltip formatter={(value, name) => name === "Total Collected" ? [currency.format(value), name] : [value, name]} /><Legend />
            <Bar yAxisId="sales" dataKey="revenue" name="Total Collected" fill="var(--ui-primary)" radius={[7, 7, 0, 0]} /><Line yAxisId="orders" dataKey="orders" name="Closed Orders" stroke="var(--ui-warning-text)" strokeWidth={3} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer> : <EmptyInsight>No sales recorded for this period.</EmptyInsight>}
      </section>

      {canViewTakeaway && <section className="dashboard-section dashboard-channel-card" aria-labelledby="channel-heading">
        <div className="dashboard-section-heading"><div><span>Channel performance</span><h2 id="channel-heading">Takeaway order source</h2></div><small>{sourceTotal} total takeaway orders</small></div>
        <div className="dashboard-channel-summary">
          <div className="dashboard-channel-values"><div><span>Customer link</span><strong>{customerOrders}</strong><small>{customerShare.toFixed(0)}%</small></div><div><span>Restaurant staff</span><strong>{staffOrders}</strong><small>{(sourceTotal ? 100 - customerShare : 0).toFixed(0)}%</small></div></div>
          <div className="dashboard-channel-track" aria-label={`${customerShare.toFixed(0)} percent customer orders`}><span style={{ width: `${customerShare}%` }} /></div>
        </div>
      </section>}

      <div className="dashboard-insight-grid">
        {canViewMenu && <section className="dashboard-section dashboard-table-card" aria-labelledby="items-heading">
          <div className="dashboard-section-heading"><div><span>Menu performance</span><h2 id="items-heading">Top selling items</h2></div></div>
          {topItems.length ? <div className="dashboard-table-scroll"><table className="dashboard-table"><thead><tr><th>Item</th><th>Qty</th><th>Sales</th></tr></thead><tbody>{topItems.map((item, index) => <tr key={`${item.name}-${index}`}><td>{item.name}</td><td>{item.quantity}</td><td>{currency.format(Number(item.revenue || 0))}</td></tr>)}</tbody></table></div> : <EmptyInsight>No item sales recorded.</EmptyInsight>}
        </section>}
        <section className="dashboard-section dashboard-table-card" aria-labelledby="payments-heading">
          <div className="dashboard-section-heading"><div><span>Reconciliation</span><h2 id="payments-heading">Payment collection</h2></div></div>
          {paymentModes.length ? <div className="dashboard-table-scroll"><table className="dashboard-table"><thead><tr><th>Payment mode</th><th>Collected</th></tr></thead><tbody>{paymentModes.map((item, index) => <tr key={`${item.paymentMode}-${index}`}><td>{item.paymentMode || "Unknown"}</td><td>{currency.format(Number(item.revenue || 0))}</td></tr>)}</tbody></table></div> : <EmptyInsight>No payment collections recorded.</EmptyInsight>}
        </section>
      </div>

      <div className="dashboard-insight-grid">
        <section className="dashboard-section dashboard-chart-card" aria-labelledby="peak-heading">
          <div className="dashboard-section-heading"><div><span>Demand</span><h2 id="peak-heading">Orders by hour</h2></div></div>
          {peakHoursData.length ? <ResponsiveContainer width="100%" height={300}><ComposedChart data={peakHoursData}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="hour" /><YAxis allowDecimals={false} /><Tooltip /><Legend /><Bar dataKey="completed" name="Closed" stackId="orders" fill="var(--ui-primary)" /><Bar dataKey="cancelled" name="Cancelled" stackId="orders" fill="var(--ui-danger)" radius={[6, 6, 0, 0]} /></ComposedChart></ResponsiveContainer> : <EmptyInsight>No hourly order data available.</EmptyInsight>}
        </section>
        {canViewMenu && <section className="dashboard-section dashboard-chart-card" aria-labelledby="category-heading">
          <div className="dashboard-section-heading"><div><span>Menu mix</span><h2 id="category-heading">Sales by category</h2></div></div>
          {categories.length ? <ResponsiveContainer width="100%" height={300}><PieChart><Pie data={categories} dataKey="revenue" nameKey="category" innerRadius={58} outerRadius={92} paddingAngle={2}>{categories.map((entry, index) => <Cell key={`${entry.category}-${index}`} fill={["#0f766e", "#2563eb", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"][index % 6]} />)}</Pie><Tooltip formatter={(value, name, props) => [currency.format(value), `${props.payload.category} (${props.payload.quantity || 0} items)`]} /><Legend /></PieChart></ResponsiveContainer> : <EmptyInsight>No category sales recorded.</EmptyInsight>}
        </section>}
      </div>
    </div>
  );
};

export default Dashboard;
